import { configuredSensorTarget, withinSensorTarget, type ConfiguredSensorTargets } from "@/app/utils/vision-sensor-targets";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Activity, AlertTriangle, CalendarDays, CheckCircle2, Droplets, FlaskConical, ScanLine, Search, Thermometer, Video, Waves, Webcam, Wifi, X, Zap } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getAlertSensorType, hasVisualEvidence, type ClinicalCase } from '@/app/utils/vision-biology';
import { t, useLanguage, type Language } from '@/app/utils/i18n';

interface VisionDashboardProps {
  cases: ClinicalCase[];
  summary?: MvpDashboardSummary | null;
  sensorReadings?: MvpDashboardSummary['sensorReadings'];
  sensorScope?: {
    clientId?: string | null;
    projectId?: string | null;
    tankId?: string | null;
  };
  onOpenAlerts: () => void;
  onOpenLatestCapture?: (alertId?: string) => void;
  onOpenLiveCamera?: () => void;
}

export interface MvpDashboardSummary {
  kitId?: string | null;
  kitName?: string | null;
  zoneId?: string | null;
  status?: 'OK' | 'Atenção' | 'Alerta' | string | null;
  reason?: string | null;
  updatedAt?: string | null;
  latestCapture?: {
    alertId?: string | null;
    imageUrl?: string | null;
    videoUrl?: string | null;
    videoMp4Url?: string | null;
    videoWebmUrl?: string | null;
    videoStartSeconds?: number | null;
    videoLoop?: boolean | null;
    capturedAt?: string | null;
    tank?: string | null;
    phase?: string | null;
    species?: string | null;
    detectionStatus?: string | null;
  } | null;
  alertsActive?: number | null;
  pendingValidation?: number | null;
  sensors?: {
    temperature?: number | null;
    ph?: number | null;
    oxygen?: number | null;
    redox?: number | null;
    salinity?: number | null;
    status?: string | null;
  } | null;
  sensorTargets?: ConfiguredSensorTargets | null;
  sensorReadings?: Array<{
    id?: string | null;
    clientId?: string | null;
    deviceId?: string | null;
    sensorId?: string | null;
    sensorType?: string | null;
    value?: number | null;
    unit?: string | null;
    timestamp?: string | null;
    site?: string | null;
    tank?: string | null;
    source?: string | null;
    status?: string | null;
  }> | null;
  device?: {
    online?: boolean | null;
    lastCommunication?: string | null;
    heartbeatIntervalSeconds?: number | null;
    batteryPercent?: number | null;
    communicationAt?: string | null;
    cameraOk?: boolean | null;
    sensorsOk?: boolean | null;
  } | null;
}

const localeByLanguage: Record<Language, string> = {
  pt: 'pt-PT',
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE'
};

const formatDateTime = (value: string | null | undefined, language: Language) => {
  if (!value) return t('vision', 'noData', language);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('vision', 'noData', language);
  return date.toLocaleString(localeByLanguage[language], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTime = (value: string | null | undefined, language: Language) => {
  if (!value) return t('vision', 'noData', language);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('vision', 'noData', language);
  return date.toLocaleTimeString(localeByLanguage[language], {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getMeta = (item: ClinicalCase | null, keys: string[]) => {
  if (!item) return null;
  for (const key of keys) {
    const value = item.rawAlert.metadata?.[key] ?? (item.rawAlert as Record<string, any>)[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const booleanFrom = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value ?? '').toLowerCase().trim();
  if (['true', 'ok', 'online', 'active', 'ativo', 'ativa'].includes(normalized)) return true;
  if (['false', 'fail', 'failed', 'offline', 'inactive', 'inativo', 'inativa', 'falha'].includes(normalized)) return false;
  return null;
};

const numberFrom = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value === null || value === undefined) return null;
  const normalized = String(value).replace(',', '.').replace(/[^\d.-]/g, '').trim();
  if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeSensorMetricValue = (metric: SensorAuditMetric | null, value: number | null) => {
  if (value === null || !Number.isFinite(value)) return null;
  if (metric === 'ph') return value > 0 && value <= 14 ? value : null;
  if (metric === 'temperature' || metric === 'oxygen' || metric === 'salinity') return value > 0 ? value : null;
  if (metric === 'redox') return value === 0 ? null : value;
  return value;
};

const heartbeatOnline = (lastCommunication: string | null | undefined, heartbeatIntervalSeconds: number) => {
  if (!lastCommunication) return null;
  const heartbeatMs = new Date(lastCommunication).getTime();
  if (!Number.isFinite(heartbeatMs)) return null;
  const ageMs = Date.now() - heartbeatMs;
  const staleAfterMs = Math.max(90, heartbeatIntervalSeconds * 3) * 1000;
  return ageMs <= staleAfterMs;
};

const sensorState = (latest: ClinicalCase | null, language: Language) => {
  if (!latest || (latest.temperature === null && latest.ph === null && latest.oxygen === null && numberFrom(getMeta(latest, ['redox', 'orp', 'redox_mv', 'orp_mv'])) === null)) {
    return { label: t('vision', 'sensorsNoFeed', language), tone: 'text-[#8a6500] dark:text-amber-200', status: 'no-feed' as const };
  }

  const outOfRange =
    (latest.temperature !== null && (latest.temperature < 16 || latest.temperature > 24)) ||
    (latest.ph !== null && (latest.ph < 7.2 || latest.ph > 8.4)) ||
    (latest.oxygen !== null && latest.oxygen < 5.5) ||
    (() => {
      const redox = numberFrom(getMeta(latest, ['redox', 'orp', 'redox_mv', 'orp_mv']));
      return redox !== null && (redox < 250 || redox > 400);
    })();

  return outOfRange
    ? { label: t('vision', 'outOfRange', language), tone: 'text-[#8a6500] dark:text-amber-200', status: 'warning' as const }
    : { label: t('vision', 'ok', language), tone: 'text-[#1f7a49] dark:text-emerald-200', status: 'ok' as const };
};

const detectionState = (latest: ClinicalCase | null, language: Language): string | null => {
  if (!latest) return null;
  const raw = String(getMeta(latest, ['detection_status', 'detectionStatus', 'fishDetection', 'fish_detected']) ?? '').trim();
  const lower = raw.toLowerCase();
  if (!raw) return null;
  if (lower.includes('erro') || lower.includes('error')) return t('common', 'error', language);
  if (lower.includes('sem') || lower.includes('none') || lower === 'false') return t('vision', 'noFish', language);
  if (['peixe detetado', 'fish detected', 'sensor ambiental', 'environmental sensor'].includes(lower)) return null;
  return raw;
};

const severityWeight = (severity: ClinicalCase['severity']) => ({
  critical: 4,
  high: 3,
  medium: 2,
  low: 1
}[severity] ?? 0);

const operationalTimestamp = (item: ClinicalCase | null | undefined) => {
  if (!item) return 0;
  const value = item.rawAlert.updated_at ?? item.rawAlert.updatedAt ?? item.rawAlert.detected_at ?? item.rawAlert.detectedAt ?? item.capturedAt;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sensorConfigFor = (sensorType: string | null) => {
  const normalized = String(sensorType ?? '').toLowerCase();
  if (normalized.includes('oxygen') || normalized.includes('oxigen') || normalized === 'o2') {
    return {
      key: 'oxygen',
      label: 'Oxigénio dissolvido',
      unit: 'mg/L',
      target: '6+ mg/L',
      min: 6,
      max: null,
      lowerImpact: 'Respiração comprometida, stress agudo e risco operacional imediato.',
      upperImpact: 'Leitura acima do esperado; confirmar calibração e estabilidade da água.'
    };
  }
  if (normalized.includes('ph')) {
    return {
      key: 'ph',
      label: 'pH',
      unit: 'pH',
      target: '7–8.2 pH',
      min: 7,
      max: 8.2,
      lowerImpact: 'Pode alterar toxicidade, equilíbrio fisiológico e resposta alimentar.',
      upperImpact: 'Pode alterar toxicidade, equilíbrio fisiológico e resposta alimentar.'
    };
  }
  if (normalized.includes('redox') || normalized.includes('orp')) {
    return {
      key: 'redox',
      label: 'Redox / ORP',
      unit: 'mV',
      target: '250–400 mV',
      min: 250,
      max: 400,
      lowerImpact: 'Sinal compatível com degradação da qualidade da água; confirmar carga orgânica e renovação.',
      upperImpact: 'Potencial oxidativo elevado; confirmar química da água e calibração do sensor.'
    };
  }
  if (normalized.includes('salin')) {
    return {
      key: 'salinity',
      label: 'Salinidade',
      unit: 'ppt',
      target: 'A confirmar por regra',
      min: null,
      max: null,
      lowerImpact: 'Variação osmótica relevante; confirmar fase, espécie e regra aplicada.',
      upperImpact: 'Variação osmótica relevante; confirmar fase, espécie e regra aplicada.'
    };
  }
  return {
    key: 'temperature',
    label: 'Temperatura',
    unit: '°C',
    target: '18–22 °C',
    min: 18,
    max: 22,
    lowerImpact: 'Pode reduzir metabolismo, apetite, atividade e alterar consumo de oxigénio.',
    upperImpact: 'Pode aumentar metabolismo, stress e consumo de oxigénio.'
  };
};

const formatReading = (value: number | null, unit: string) => {
  if (value === null) return 'Sem leitura';
  const decimals = unit === 'pH' ? 2 : unit === 'mV' ? 0 : value % 1 === 0 ? 0 : 2;
  const formatted = decimals === 0
    ? value.toFixed(0)
    : value.toFixed(decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return `${formatted} ${unit}`;
};

const cleanRuleText = (value: unknown) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

type SensorAuditMetric = 'temperature' | 'ph' | 'oxygen' | 'redox' | 'salinity';
type SensorAuditRange = 'day' | 'week' | 'month';
type SensorAuditSort = 'newest' | 'oldest' | 'value_desc' | 'value_asc' | 'tank';

type SensorAuditRow = {
  id: string;
  timestamp: string;
  site: string;
  tank: string;
  status: string;
  alertId: string;
  metrics: Record<SensorAuditMetric, number | null>;
};

const SENSOR_AUDIT_CONFIG: Record<SensorAuditMetric, {
  label: string;
  unit: string;
  keys: string[];
  targetMin: number;
  targetMax: number | null;
  targetLabel: string;
  color: string;
}> = {
  temperature: {
    label: 'Temperatura',
    unit: '°C',
    targetMin: 18,
    targetMax: 22,
    targetLabel: '18–22 °C',
    color: '#fb923c',
    keys: ['temperature', 'temperatura', 'temp', 'water_temperature', 'water_temperature_c', 'temperature_c', 'temp_c', 'sensor_value', 'sensorValue', 'reading_value', 'readingValue', 'value']
  },
  ph: {
    label: 'pH',
    unit: 'pH',
    targetMin: 7,
    targetMax: 8.2,
    targetLabel: '7–8.2 pH',
    color: '#38bdf8',
    keys: ['ph', 'pH', 'sensor_value', 'sensorValue', 'reading_value', 'readingValue', 'value']
  },
  oxygen: {
    label: 'Oxigénio dissolvido',
    unit: 'mg/L',
    targetMin: 6,
    targetMax: null,
    targetLabel: '≥ 6 mg/L',
    color: '#34d399',
    keys: ['oxygen', 'dissolved_oxygen', 'oxygen_mg_l', 'do_mg_l', 'o2', 'sensor_value', 'sensorValue', 'reading_value', 'readingValue', 'value']
  },
  redox: {
    label: 'Redox / ORP',
    unit: 'mV',
    targetMin: 250,
    targetMax: 400,
    targetLabel: '250–400 mV',
    color: '#a78bfa',
    keys: ['redox', 'orp', 'redox_mv', 'orp_mv', 'sensor_value', 'sensorValue', 'reading_value', 'readingValue', 'value']
  },
  salinity: {
    label: 'Salinidade',
    unit: 'PSU',
    targetMin: 28,
    targetMax: 37,
    targetLabel: '28–37 PSU',
    color: '#2dd4bf',
    keys: ['salinity', 'salinidade', 'salinidad', 'salt', 'psu', 'ppt', 'sensor_value', 'sensorValue', 'reading_value', 'readingValue', 'value']
  }
};

const sensorTypeMatchesMetric = (sensorType: string | null | undefined, metric: SensorAuditMetric) => {
  const normalized = String(sensorType ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ');
  if (!normalized) return false;
  return SENSOR_AUDIT_CONFIG[metric].keys.some((alias) => {
    const normalizedAlias = alias
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ');
    return normalized === normalizedAlias || normalized.includes(normalizedAlias);
  });
};

const sensorTypeFromUnit = (unit: string | null | undefined): SensorAuditMetric | null => {
  const normalized = String(unit ?? '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'c' || normalized === '°c' || normalized.includes('celsius')) return 'temperature';
  if (normalized === 'ph') return 'ph';
  if (normalized.includes('mg/l') || normalized.includes('mg l')) return 'oxygen';
  if (normalized === 'mv' || normalized.includes('millivolt')) return 'redox';
  if (normalized === 'psu' || normalized === 'ppt') return 'salinity';
  return null;
};

const metricFromSensorType = (sensorType: string | null | undefined): SensorAuditMetric | null =>
  (Object.keys(SENSOR_AUDIT_CONFIG) as SensorAuditMetric[]).find((metric) =>
    sensorTypeMatchesMetric(sensorType, metric)
  ) ?? null;

const isoDateOnly = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dayBounds = (dateText: string) => {
  const date = dateText ? new Date(`${dateText}T00:00:00`) : new Date();
  const start = Number.isNaN(date.getTime()) ? new Date() : date;
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start, end };
};

const rangeBounds = (range: SensorAuditRange, dateText: string) => {
  const { start, end } = dayBounds(dateText);
  if (range === 'day') return { start, end };
  const expandedStart = new Date(start);
  expandedStart.setDate(expandedStart.getDate() - (range === 'week' ? 6 : 29));
  return { start: expandedStart, end };
};

const buildSensorAuditRows = (_cases: ClinicalCase[], summary?: MvpDashboardSummary | null): SensorAuditRow[] => {
  const tableReadingRows = (summary?.sensorReadings ?? [])
    .map((reading): SensorAuditRow | null => {
      const metric = metricFromSensorType(reading.sensorType) ?? sensorTypeFromUnit(reading.unit);
      const value = normalizeSensorMetricValue(metric, reading.value ?? null);
      if (!metric || value === null || !reading.timestamp) return null;
      return {
        id: reading.id ?? `${metric}-${reading.timestamp}`,
        timestamp: reading.timestamp,
        site: reading.site ?? 'Instalação não indicada',
        tank: reading.tank ?? 'Tanque não indicado',
        status: reading.source === 'Demonstração' ? 'Simulado' : 'Leitura registada',
        alertId: reading.id ?? 'leitura-ambiental',
        metrics: {
          temperature: metric === 'temperature' ? value : null,
          ph: metric === 'ph' ? value : null,
          oxygen: metric === 'oxygen' ? value : null,
          redox: metric === 'redox' ? value : null,
          salinity: metric === 'salinity' ? value : null
        }
      };
    })
    .filter((row): row is SensorAuditRow => Boolean(row));

  return tableReadingRows.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

const formatSensorAuditValue = (value: number | null, metric: SensorAuditMetric) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const unit = SENSOR_AUDIT_CONFIG[metric].unit;
  const decimals = metric === 'ph' ? 2 : metric === 'redox' ? 0 : 1;
  return `${value.toFixed(decimals)} ${unit}`;
};

const buildSensorAuditChartData = (
  rows: SensorAuditRow[],
  metric: SensorAuditMetric,
  range: SensorAuditRange
) => {
  const bucketSizeMs = range === 'day'
    ? 15 * 60 * 1000
    : range === 'week'
      ? 3 * 60 * 60 * 1000
      : 24 * 60 * 60 * 1000;
  const buckets = new Map<number, number[]>();

  rows.forEach((row) => {
    const timestamp = new Date(row.timestamp).getTime();
    const value = row.metrics[metric];
    if (!Number.isFinite(timestamp) || value === null || !Number.isFinite(value)) return;
    const bucket = Math.floor(timestamp / bucketSizeMs) * bucketSizeMs;
    const values = buckets.get(bucket) ?? [];
    values.push(value);
    buckets.set(bucket, values);
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([timestamp, values]) => ({
      timestamp,
      value: values.reduce((sum, value) => sum + value, 0) / values.length,
      samples: values.length,
      label: new Intl.DateTimeFormat('pt-PT', range === 'month'
        ? { day: '2-digit', month: '2-digit' }
        : { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
      ).format(new Date(timestamp))
    }));
};

const latestSummarySensorReading = (summary: MvpDashboardSummary | null | undefined, metric: SensorAuditMetric) => {
  const rows = (summary?.sensorReadings ?? [])
    .filter((row) => {
      const rowMetric = metricFromSensorType(row.sensorType) ?? sensorTypeFromUnit(row.unit);
      return rowMetric === metric &&
        normalizeSensorMetricValue(metric, row.value ?? null) !== null &&
        Boolean(row.timestamp);
    })
    .sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime());
  return normalizeSensorMetricValue(metric, rows[0]?.value ?? null);
};

const latestMatchingSensorReading = (
  summary: MvpDashboardSummary | null | undefined,
  metric: SensorAuditMetric,
  sensorId?: string | null
) => {
  const expectedSensorId = String(sensorId ?? '').trim();
  const rows = (summary?.sensorReadings ?? [])
    .filter((row) => {
      const rowMetric = metricFromSensorType(row.sensorType) ?? sensorTypeFromUnit(row.unit);
      if (rowMetric !== metric || normalizeSensorMetricValue(metric, row.value ?? null) === null || !row.timestamp) return false;
      const sameSensor = expectedSensorId && String(row.sensorId ?? '').trim() === expectedSensorId;
      return sameSensor || rowMetric === metric;
    })
    .sort((a, b) => {
      const aSameSensor = expectedSensorId && String(a.sensorId ?? '').trim() === expectedSensorId;
      const bSameSensor = expectedSensorId && String(b.sensorId ?? '').trim() === expectedSensorId;
      if (aSameSensor !== bSameSensor) return bSameSensor ? 1 : -1;
      return new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime();
    });
  return normalizeSensorMetricValue(metric, rows[0]?.value ?? null);
};

const mergeSensorReadings = (
  primary: MvpDashboardSummary['sensorReadings'],
  fallback: MvpDashboardSummary['sensorReadings']
): MvpDashboardSummary['sensorReadings'] => {
  const rows = [...(fallback ?? []), ...(primary ?? [])];
  if (!rows.length) return null;
  return Array.from(
    new Map(
      rows
        .sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime())
        .map((row, index) => [row.id || `${row.sensorType ?? 'sensor'}-${row.timestamp ?? index}-${index}`, row])
    ).values()
  );
};

const sensorValueFromRows = (rows: MvpDashboardSummary['sensorReadings'], metric: SensorAuditMetric) => {
  const latest = (rows ?? [])
    .filter((row) => {
      const rowMetric = metricFromSensorType(row.sensorType) ?? sensorTypeFromUnit(row.unit);
      return rowMetric === metric &&
        normalizeSensorMetricValue(metric, row.value ?? null) !== null &&
        Boolean(row.timestamp);
    })
    .sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime())[0];
  return normalizeSensorMetricValue(metric, latest?.value ?? null);
};

const latestSensorTimestamp = (rows: MvpDashboardSummary['sensorReadings']) =>
  (rows ?? [])
    .map((row) => row.timestamp)
    .filter((value): value is string => Boolean(value) && Number.isFinite(new Date(value).getTime()))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;

function DashboardCard({
  title,
  children,
  className = '',
  demoTour,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  demoTour?: string;
}) {
  return (
    <section
      data-demo-tour={demoTour}
      className={`aql-vision-dashboard-card flex h-full min-h-0 flex-col rounded-2xl border border-border bg-card/86 p-4 shadow-sm ring-1 ring-border/35 backdrop-blur-sm lg:p-5 ${className}`}
    >
      <h2 className="shrink-0 text-[0.72rem] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {title}
      </h2>
      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        {children}
      </div>
    </section>
  );
}

function MetricRow({ label, value, compact = false }: { label: string; value: ReactNode; compact?: boolean }) {
  return (
    <div className={`aql-vision-dashboard-row flex items-center rounded-xl !border-0 bg-muted/30 ${compact ? 'gap-2 px-2.5 py-1.5' : 'gap-3 px-3 py-2.5'}`}>
      <span className={`flex-1 font-medium text-slate-500 dark:text-slate-400 ${compact ? 'text-[0.72rem]' : 'text-xs'}`}>{label}</span>
      <span className={`shrink-0 text-right font-extrabold text-slate-950 dark:text-white ${compact ? 'text-xs' : 'text-sm'}`}>{value}</span>
    </div>
  );
}

function StatusValueBadge({ children, tone = 'slate' }: { children: ReactNode; tone?: 'amber' | 'red' | 'emerald' | 'slate' | 'cyan' | 'purple' }) {
  const toneClass = {
    amber: 'border-amber-300 bg-amber-100 text-amber-950 dark:border-amber-400/40 dark:bg-amber-400 dark:text-slate-950',
    red: 'border-red-200 bg-red-50 text-red-700 dark:border-red-400/45 dark:bg-red-500/16 dark:text-red-200',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/45 dark:bg-emerald-500/16 dark:text-emerald-200',
    slate: 'border-border bg-muted text-foreground dark:border-slate-500/45 dark:bg-slate-500/22 dark:text-slate-100',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-400/45 dark:bg-cyan-500/16 dark:text-cyan-200',
    purple: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-400/45 dark:bg-purple-500/16 dark:text-purple-200'
  }[tone];

  return (
    <span className={`inline-flex min-w-10 justify-center rounded-xl border px-3 py-1 text-sm font-extrabold ${toneClass} ${tone === 'emerald' ? 'dark:shadow-[0_0_18px_rgba(16,185,129,0.22)]' : ''} ${tone === 'red' ? 'dark:shadow-[0_0_18px_rgba(248,113,113,0.24)]' : ''}`}>
      {children}
    </span>
  );
}

function OperationalRow({
  icon: Icon,
  label,
  value,
  tone = 'slate'
}: {
  icon?: typeof AlertTriangle;
  label: string;
  value: ReactNode;
  tone?: 'amber' | 'red' | 'emerald' | 'slate' | 'cyan' | 'purple';
}) {
  const iconTone = {
    amber: 'text-amber-400',
    red: 'text-red-300',
    emerald: 'text-emerald-300',
    slate: 'text-slate-400',
    cyan: 'text-cyan-300',
    purple: 'text-purple-300'
  }[tone];

  return (
    <div className="aql-vision-dashboard-row flex min-h-[4.2rem] items-center gap-3 rounded-xl !border-0 bg-muted/25 px-4 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {Icon ? <Icon className={`h-5 w-5 shrink-0 ${iconTone}`} /> : null}
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className="shrink-0 text-right text-sm font-extrabold text-slate-950 dark:text-white">{value}</div>
    </div>
  );
}

function SensorSignalCard({
  icon: Icon,
  label,
  value,
  unit,
  target,
  hasFeed,
  sourceLabel,
  tone,
  isCompliant,
  language,
  compact = false,
  onClick
}: {
  icon: typeof Thermometer;
  label: string;
  value: string;
  unit?: string;
  target: string;
  hasFeed: boolean;
  sourceLabel?: string;
  tone: 'blue' | 'cyan' | 'purple' | 'emerald';
  trend: number[];
  isCompliant?: boolean;
  language: Language;
  compact?: boolean;
  onClick?: () => void;
}) {
  const cardBackgroundClass = {
    blue: 'border-blue-200/75 bg-gradient-to-br from-blue-50/60 via-card to-card dark:border-blue-500/25 dark:from-blue-500/10 dark:via-card dark:to-card',
    cyan: 'border-cyan-200/75 bg-gradient-to-br from-cyan-50/60 via-card to-card dark:border-cyan-500/25 dark:from-cyan-500/10 dark:via-card dark:to-card',
    purple: 'border-purple-200/75 bg-gradient-to-br from-purple-50/60 via-card to-card dark:border-purple-500/25 dark:from-purple-500/10 dark:via-card dark:to-card',
    emerald: 'border-emerald-200/75 bg-gradient-to-br from-emerald-50/60 via-card to-card dark:border-emerald-500/25 dark:from-emerald-500/10 dark:via-card dark:to-card'
  }[tone];

  const iconBackgroundClass = {
    blue: 'border-blue-200/70 bg-blue-50/80 text-blue-700 dark:border-blue-500/25 dark:bg-blue-500/10 dark:text-blue-300',
    cyan: 'border-cyan-200/70 bg-cyan-50/80 text-cyan-700 dark:border-cyan-500/25 dark:bg-cyan-500/10 dark:text-cyan-300',
    purple: 'border-purple-200/70 bg-purple-50/80 text-purple-700 dark:border-purple-500/25 dark:bg-purple-500/10 dark:text-purple-300',
    emerald: 'border-emerald-200/70 bg-emerald-50/80 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-300'
  }[tone];

  const valueColorClass = {
    blue: 'text-blue-700 dark:text-blue-300',
    cyan: 'text-cyan-700 dark:text-cyan-300',
    purple: 'text-purple-700 dark:text-purple-300',
    emerald: 'text-emerald-700 dark:text-emerald-300'
  }[tone];

  const hasKnownValue = hasFeed || Boolean(sourceLabel);
  const statusBadgeClass = !hasKnownValue
    ? 'border-border bg-muted/30 text-muted-foreground'
    : isCompliant === false
      ? 'border-amber-200/80 bg-amber-50/60 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/12 dark:text-amber-100'
      : 'border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-500/25 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
  const statusBadgeLabel = sourceLabel ?? (!hasFeed
    ? t('vision', 'noFeed', language)
    : isCompliant === false
      ? 'Fora do alvo'
      : isCompliant === true
        ? 'Dentro do alvo'
        : t('vision', 'receiving', language));

  const isDisabled = !hasKnownValue;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`aql-vision-dashboard-sensor flex flex-col rounded-xl border text-left shadow-sm ring-1 ring-border/45 transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cyan-500/35 ${compact ? 'min-h-[6.25rem] p-2.5' : 'min-h-[10rem] p-3 lg:p-4'} ${cardBackgroundClass} ${isDisabled ? 'opacity-45 grayscale' : ''}`}
      aria-label={`Abrir histórico de ${label}`}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="aql-vision-dashboard-sensor-label text-[10px] font-semibold uppercase tracking-[0.09em] text-foreground/70">{label}</p>
          <p className={`aql-vision-dashboard-sensor-value mt-1.5 flex flex-wrap items-baseline gap-1 font-semibold leading-none tracking-tight ${compact ? "text-xl" : "text-2xl"} ${valueColorClass}`}>
            {value}
            {unit ? <span className="text-sm font-medium text-foreground/70">{unit}</span> : null}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${iconBackgroundClass}`}>
            <Icon className="h-3 w-3" />
          </span>
        </div>
      </div>
      <p className="aql-vision-dashboard-sensor-target mt-2 text-[11px] font-semibold text-foreground/90">{t('vision', 'target', language)}: {target}</p>
      <div className="mt-2"><span className={`aql-vision-dashboard-sensor-badge inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${statusBadgeClass}`}>
            {statusBadgeLabel}
          </span></div>
    </button>
  );
}

function LatestCaptureMedia({
  capture,
  label,
  onOpen,
  language
}: {
  capture: NonNullable<MvpDashboardSummary['latestCapture']>;
  label: string;
  onOpen?: () => void;
  language: Language;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const primedRef = useRef(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const startSeconds = typeof capture.videoStartSeconds === 'number' && Number.isFinite(capture.videoStartSeconds)
    ? Math.max(0, capture.videoStartSeconds)
    : null;
  const hasVideo = !videoFailed && Boolean(capture.videoUrl || capture.videoMp4Url || capture.videoWebmUrl);

  useEffect(() => {
    primedRef.current = false;
    setVideoFailed(false);
  }, [capture.alertId, capture.videoMp4Url, capture.videoStartSeconds, capture.videoUrl, capture.videoWebmUrl]);

  const cueAndPlay = (video: HTMLVideoElement) => {
    video.muted = true;
    video.volume = 0;
    if (!primedRef.current && startSeconds !== null) {
      primedRef.current = true;
      video.currentTime = startSeconds;
    }
    video.play().catch(() => undefined);
  };

  const keepPreviewSynced = (video: HTMLVideoElement) => {
    if (!capture.videoLoop || startSeconds === null) return;
    const loopEnd = startSeconds + 7;
    if (video.currentTime >= loopEnd || video.currentTime < Math.max(0, startSeconds - 0.35)) {
      video.currentTime = startSeconds;
      video.play().catch(() => undefined);
    }
  };

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className="aql-vision-dashboard-media group block h-56 w-full cursor-pointer text-left disabled:cursor-default"
      title={t('vision', 'openDetection', language)}
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          className="h-56 w-full object-cover"
          autoPlay
          muted
          playsInline
          loop={Boolean(capture.videoLoop && startSeconds === null)}
          preload="metadata"
          poster={capture.imageUrl ?? undefined}
          controls={false}
          disablePictureInPicture
          aria-label={label}
          onLoadedMetadata={(event) => cueAndPlay(event.currentTarget)}
          onCanPlay={(event) => cueAndPlay(event.currentTarget)}
          onTimeUpdate={(event) => keepPreviewSynced(event.currentTarget)}
          onError={() => setVideoFailed(true)}
          onVolumeChange={(event) => {
            event.currentTarget.muted = true;
            event.currentTarget.volume = 0;
          }}
        >
          {capture.videoMp4Url ? <source src={capture.videoMp4Url} type="video/mp4" /> : null}
          {capture.videoWebmUrl ? <source src={capture.videoWebmUrl} type="video/webm" /> : null}
          {capture.videoUrl ? <source src={capture.videoUrl} /> : null}
        </video>
      ) : capture.imageUrl ? (
        <img src={capture.imageUrl} alt={label} className="h-56 w-full object-cover" />
      ) : (
        <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">{label}</div>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-b from-transparent to-slate-900/[0.11] dark:to-slate-950/35" />
      {onOpen ? (
        <div className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full border border-white/25 bg-slate-950/70 px-3 py-1.5 text-xs font-semibold text-white opacity-0 shadow-sm backdrop-blur transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <ScanLine className="h-3.5 w-3.5" />
          {t('vision', 'viewDetection', language)}
        </div>
      ) : null}
    </button>
  );
}

export function VisionDashboard({ cases, summary, sensorReadings = null, sensorScope, onOpenAlerts, onOpenLatestCapture, onOpenLiveCamera }: VisionDashboardProps) {
  const language = useLanguage();
  const [, setHeartbeatTick] = useState(0);
  const [activeSensorAudit, setActiveSensorAudit] = useState<SensorAuditMetric | null>(null);
  const [sensorAuditRange, setSensorAuditRange] = useState<SensorAuditRange>('day');
  const [sensorAuditDate, setSensorAuditDate] = useState(() => isoDateOnly(new Date()));
  const [sensorAuditSort, setSensorAuditSort] = useState<SensorAuditSort>('newest');
  const [directSensorReadings, setDirectSensorReadings] = useState<MvpDashboardSummary['sensorReadings']>(sensorReadings ?? null);

  useEffect(() => {
    const timer = window.setInterval(() => setHeartbeatTick((value) => value + 1), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setDirectSensorReadings(sensorReadings ?? null);
  }, [sensorReadings, sensorScope?.clientId, sensorScope?.projectId, sensorScope?.tankId]);

  const [demoRecordedAt] = useState(() => Date.now());
  const registeredSensorReadings = mergeSensorReadings(directSensorReadings, summary?.sensorReadings ?? null);
  // Local KPI prototype only: never replace registered values or seed production data.
  const demoEnabled = import.meta.env.DEV
    && ['127.0.0.1', 'localhost'].includes(window.location.hostname)
    && sensorScope?.clientId === 'ba8c40f9-7a6c-444f-8af0-baa5bd9372bb'
    && sensorScope?.projectId === '931d9f87-4c3a-4bad-8afe-57e4e69f835c'
    && (!sensorScope?.tankId || sensorScope.tankId === '5fee532d-de72-497c-9a14-07f38c5b105f');
  const demoProfiles = [
    { metric: 'ph', value: 8.05, amplitude: 0.06, unit: 'pH' },
    { metric: 'oxygen', value: 8.6, amplitude: 0.35, unit: 'mg/L' },
    { metric: 'redox', value: 300, amplitude: 15, unit: 'mV' },
    { metric: 'salinity', value: 33, amplitude: 0.2, unit: 'PSU' }
  ] as const;
  const demoMetrics = new Set(demoProfiles.filter(({ metric }) => demoEnabled
    && sensorValueFromRows(registeredSensorReadings, metric) === null
    && numberFrom(summary?.sensors?.[metric]) === null).map(({ metric }) => metric));
  const demoReadings = demoProfiles.filter(({ metric }) => demoMetrics.has(metric)).flatMap((profile) =>
    Array.from({ length: 289 }, (_, index) => ({
      id: `local-cod-demo-${profile.metric}-${index}`,
      clientId: sensorScope?.clientId,
      sensorType: profile.metric,
      value: Number((profile.value + profile.amplitude * Math.sin(index / 18) * Math.sin(index / 45)).toFixed(2)),
      unit: profile.unit,
      timestamp: new Date(demoRecordedAt - index * 5 * 60_000).toISOString(),
      site: 'AQL · demonstração local',
      tank: 'Bacalhau adulto · simulação',
      source: 'Demonstração',
      status: 'Simulado'
    }))
  );
  const effectiveSensorReadings = mergeSensorReadings(registeredSensorReadings, demoReadings);
  const effectiveTemperature = sensorValueFromRows(effectiveSensorReadings, 'temperature');
  const effectivePh = sensorValueFromRows(effectiveSensorReadings, 'ph');
  const effectiveOxygen = sensorValueFromRows(effectiveSensorReadings, 'oxygen');
  const effectiveRedox = sensorValueFromRows(effectiveSensorReadings, 'redox');
  const effectiveSalinity = sensorValueFromRows(effectiveSensorReadings, 'salinity');
  const summaryTemperature = normalizeSensorMetricValue('temperature', numberFrom(summary?.sensors?.temperature));
  const summaryPh = normalizeSensorMetricValue('ph', numberFrom(summary?.sensors?.ph));
  const summaryOxygen = normalizeSensorMetricValue('oxygen', numberFrom(summary?.sensors?.oxygen));
  const summaryRedox = normalizeSensorMetricValue('redox', numberFrom(summary?.sensors?.redox));
  const summarySalinity = normalizeSensorMetricValue('salinity', numberFrom(summary?.sensors?.salinity));
  const hasEffectiveSensorFeed = [effectiveTemperature, effectivePh, effectiveOxygen, effectiveRedox, effectiveSalinity]
    .some((value) => value !== null);
  const effectiveSummary: MvpDashboardSummary | null = summary
    ? {
        ...summary,
        updatedAt: hasEffectiveSensorFeed ? latestSensorTimestamp(effectiveSensorReadings) ?? summary.updatedAt : summary.updatedAt,
        sensors: {
          ...(summary.sensors ?? {}),
          temperature: effectiveTemperature ?? summaryTemperature,
          ph: effectivePh ?? summaryPh,
          oxygen: effectiveOxygen ?? summaryOxygen,
          redox: effectiveRedox ?? summaryRedox,
          salinity: effectiveSalinity ?? summarySalinity,
          status: hasEffectiveSensorFeed ? 'OK' : summary.sensors?.status ?? null
        },
        sensorReadings: effectiveSensorReadings
      }
    : hasEffectiveSensorFeed
      ? {
          updatedAt: latestSensorTimestamp(effectiveSensorReadings),
          sensors: {
            temperature: effectiveTemperature,
            ph: effectivePh,
            oxygen: effectiveOxygen,
            redox: effectiveRedox,
            salinity: effectiveSalinity,
            status: 'OK'
          },
          sensorReadings: effectiveSensorReadings
        }
      : null;
  const sensorSummary = effectiveSummary ?? summary;

  const latestCapture = [...cases].sort((a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime())[0] ?? null;
  const casePendingValidation = cases.filter((item) => item.validationStatus === 'pending').length;
  const activeCases = cases.filter((item) => !['confirmed', 'rejected', 'resolved', 'closed', 'discarded'].includes(String(item.validationStatus ?? '').toLowerCase()));
  const caseActiveAlerts = activeCases.length;
  const activeAlerts = cases.length > 0 ? caseActiveAlerts : sensorSummary?.alertsActive ?? 0;
  const pendingValidation = cases.length > 0 ? casePendingValidation : sensorSummary?.pendingValidation ?? 0;
  const severeAlerts = activeCases.filter((item) => item.severity === 'critical' || item.severity === 'high').length;
  const latestUpdate = cases
    .map((item) => item.rawAlert.updated_at ?? item.rawAlert.updatedAt ?? item.capturedAt)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? latestCapture?.capturedAt ?? null;
  const fallbackSensors = sensorState(latestCapture, language);
  const summarySensorStatus = String(sensorSummary?.sensors?.status ?? '').trim().toLowerCase();
  const sensors = sensorSummary?.sensors
    ? {
        label: summarySensorStatus === 'ok'
          ? t('vision', 'ok', language)
          : sensorSummary.sensors.status
            ? t('vision', 'sensorsNoFeed', language)
            : fallbackSensors.label,
        tone: summarySensorStatus === 'ok' ? 'text-[#1f7a49] dark:text-emerald-200' : sensorSummary.sensors.status ? 'text-[#8a6500] dark:text-amber-200' : fallbackSensors.tone,
        status: sensorSummary.sensors.status ? 'summary' as const : fallbackSensors.status
      }
    : fallbackSensors;
  const lastCommunication = sensorSummary?.device?.lastCommunication ?? String(getMeta(latestCapture, ['lastCommunication', 'last_communication', 'lastSeenAt', 'last_seen_at', 'heartbeat']) ?? latestCapture?.capturedAt ?? '');
  const heartbeatIntervalSeconds = Math.max(
    15,
    numberFrom(sensorSummary?.device?.heartbeatIntervalSeconds)
      ?? numberFrom(getMeta(latestCapture, ['heartbeatIntervalSeconds', 'heartbeat_interval_seconds']))
      ?? 120
  );
  const rawDeviceOnline = sensorSummary?.device?.online ?? booleanFrom(getMeta(latestCapture, ['deviceOnline', 'device_online', 'kitOnline', 'kit_online', 'status', 'deviceStatus', 'kitStatus']));
  const deviceOnline = heartbeatOnline(lastCommunication, heartbeatIntervalSeconds) ?? rawDeviceOnline;
  const communicationAt = sensorSummary?.device?.communicationAt ?? String(getMeta(latestCapture, ['communicationAt', 'communication_at', 'lastSync', 'last_sync']) ?? lastCommunication ?? '');
  const cameraOk = sensorSummary?.device?.cameraOk ?? booleanFrom(getMeta(latestCapture, ['cameraOk', 'camera_ok', 'cameraStatus', 'camera_status']));
  const sensorsOk = sensorSummary?.device?.sensorsOk ?? (sensors.status === 'no-feed' ? null : sensors.status === 'ok' || sensorSummary?.sensors?.status === 'OK');
  const summaryStatus = String(deviceOnline === false ? t('vision', 'alert', language) : sensorSummary?.status ?? '').trim().toLowerCase();
  const overall =
    sensorSummary?.status
      ? {
          label: summaryStatus === 'ok'
            ? t('vision', 'ok', language)
            : summaryStatus === 'alerta' || summaryStatus === 'alert'
              ? t('vision', 'alert', language)
              : t('vision', 'attention', language),
          tone: summaryStatus === 'ok'
            ? 'border-[rgba(47,191,113,0.22)] bg-[rgba(47,191,113,0.08)] text-[#1f7a49] dark:border-emerald-500/35 dark:bg-emerald-500/12 dark:text-emerald-100'
            : summaryStatus === 'alerta' || summaryStatus === 'alert'
              ? 'border-[rgba(214,69,69,0.24)] bg-[rgba(214,69,69,0.08)] text-[#8f2727] dark:border-red-500/35 dark:bg-red-500/12 dark:text-red-100'
              : 'border-[rgba(242,183,5,0.22)] bg-[rgba(242,183,5,0.08)] text-[#8a6500] dark:border-amber-500/35 dark:bg-amber-500/12 dark:text-amber-100',
          icon: summaryStatus === 'ok' ? CheckCircle2 : AlertTriangle
        }
      : severeAlerts > 0
      ? { label: t('vision', 'alert', language), tone: 'border-[rgba(214,69,69,0.24)] bg-[rgba(214,69,69,0.08)] text-[#8f2727] dark:border-red-500/35 dark:bg-red-500/12 dark:text-red-100', icon: AlertTriangle }
      : activeAlerts > 0 || sensors.status === 'warning'
        ? { label: t('vision', 'attention', language), tone: 'border-[rgba(242,183,5,0.22)] bg-[rgba(242,183,5,0.08)] text-[#8a6500] dark:border-amber-500/35 dark:bg-amber-500/12 dark:text-amber-100', icon: AlertTriangle }
        : { label: t('vision', 'ok', language), tone: 'border-[rgba(47,191,113,0.22)] bg-[rgba(47,191,113,0.08)] text-[#1f7a49] dark:border-emerald-500/35 dark:bg-emerald-500/12 dark:text-emerald-100', icon: CheckCircle2 };
  const latestCaptureView = sensorSummary?.latestCapture ?? (latestCapture ? {
    alertId: String(latestCapture.rawAlert.case_id ?? latestCapture.rawAlert.id ?? latestCapture.rawAlert.alert_id ?? latestCapture.id ?? ''),
    imageUrl: latestCapture.image,
    capturedAt: latestCapture.capturedAt,
    tank: latestCapture.tank,
    phase: latestCapture.phase,
    species: latestCapture.species,
    detectionStatus: detectionState(latestCapture, language)
  } : null);
  const latestCaptureAlertId = latestCaptureView?.alertId ?? (latestCapture
    ? String(latestCapture.rawAlert.case_id ?? latestCapture.rawAlert.id ?? latestCapture.rawAlert.alert_id ?? latestCapture.id ?? '')
    : undefined);
  const latestCaseMetric = (
    metric: SensorAuditMetric,
    keys: string[],
    directValue: (item: ClinicalCase) => number | null | undefined
  ) => {
    const genericSensorValueKeys = ['sensor_value', 'sensorValue', 'reading_value', 'readingValue', 'value'];
    const sortedCases = [...activeCases, ...cases]
      .filter((item, index, items) => items.findIndex((candidate) => candidate.id === item.id) === index)
      .sort((a, b) => operationalTimestamp(b) - operationalTimestamp(a));
    for (const item of sortedCases) {
      const alertSensorType = getAlertSensorType(item.rawAlert);
      const specificValue = numberFrom(getMeta(item, keys)) ?? directValue(item);
      const genericValue = sensorTypeMatchesMetric(alertSensorType, metric)
        ? numberFrom(getMeta(item, genericSensorValueKeys))
        : null;
      const value = specificValue ?? genericValue;
      if (value !== null && value !== undefined) return value;
    }
    return null;
  };
  const alertTemperature = latestCaseMetric(
    'temperature',
    ['temperature', 'temp', 'water_temperature', 'water_temperature_c', 'temperature_c', 'temp_c'],
    (item) => item.temperature
  );
  const alertPh = latestCaseMetric('ph', ['ph', 'pH'], (item) => item.ph);
  const alertOxygen = latestCaseMetric('oxygen', ['oxygen', 'dissolved_oxygen', 'oxygen_mg_l', 'do_mg_l', 'o2'], (item) => item.oxygen);
  const alertRedox = latestCaseMetric('redox', ['redox', 'orp', 'redox_mv', 'orp_mv'], () => null);
  
  const liveTemperature = effectiveTemperature ?? latestCapture?.temperature ?? null;
  const livePh = effectivePh ?? latestCapture?.ph ?? null;
  const liveOxygen = effectiveOxygen ?? latestCapture?.oxygen ?? null;
  const liveRedox = effectiveRedox ?? numberFrom(getMeta(latestCapture, ['redox', 'orp', 'redox_mv', 'orp_mv'])) ?? null;
  const liveSalinity = effectiveSalinity ?? numberFrom(getMeta(latestCapture, ['salinity', 'salinidade', 'salinidad', 'salt', 'psu', 'ppt'])) ?? null;
  
  const temperature = liveTemperature ?? alertTemperature;
  const ph = livePh ?? alertPh;
  const oxygen = liveOxygen ?? alertOxygen;
  const redox = liveRedox ?? alertRedox;
  const salinity = liveSalinity ?? sensorSummary?.sensors?.salinity ?? null;
  const hasLiveTemperature = liveTemperature !== null && liveTemperature !== undefined;
  const hasLivePh = livePh !== null && livePh !== undefined;
  const hasLiveOxygen = liveOxygen !== null && liveOxygen !== undefined;
  const hasLiveRedox = liveRedox !== null && liveRedox !== undefined && liveRedox !== 0;
  const hasLiveSalinity = liveSalinity !== null && liveSalinity !== undefined;
  const sensorTargets = {
    temperature:configuredSensorTarget('temperature',sensorSummary?.sensorTargets),
    ph:configuredSensorTarget('ph',sensorSummary?.sensorTargets),
    oxygen:configuredSensorTarget('oxygen',sensorSummary?.sensorTargets),
    redox:configuredSensorTarget('redox',sensorSummary?.sensorTargets),
    salinity:configuredSensorTarget('salinity',sensorSummary?.sensorTargets),
  };
  const latestReadingSource=[...(effectiveSensorReadings || [])].sort((a,b)=>new Date(b.timestamp||0).getTime()-new Date(a.timestamp||0).getTime())[0]?.source;
  const isDemonstration=latestReadingSource==='Demonstração';
  const sortedActiveCases = [...activeCases].sort((a, b) => {
    const recencyDelta = operationalTimestamp(b) - operationalTimestamp(a);
    if (recencyDelta !== 0) return recencyDelta;
    return severityWeight(b.severity) - severityWeight(a.severity);
  });
  const primaryAlertCase = activeAlerts > 0 ? sortedActiveCases[0] ?? null : null;
  const primaryLiveSensorType = hasLiveTemperature
    ? 'temperature'
    : hasLivePh
      ? 'ph'
      : hasLiveOxygen
        ? 'dissolved_oxygen'
        : hasLiveRedox
          ? 'redox'
          : hasLiveSalinity
            ? 'salinity'
            : null;
  const primarySensorId = String(getMeta(primaryAlertCase, ['sensor_id', 'sensorId']) ?? '').trim() || null;
  const primarySensorType = activeAlerts > 0 ? getAlertSensorType(primaryAlertCase?.rawAlert) ?? (
    temperature !== null && temperature !== undefined && withinSensorTarget(temperature,sensorTargets.temperature) === false
      ? 'temperature'
      : ph !== null && ph !== undefined && withinSensorTarget(ph,sensorTargets.ph) === false
        ? 'ph'
        : oxygen !== null && oxygen !== undefined && withinSensorTarget(oxygen,sensorTargets.oxygen) === false
          ? 'dissolved_oxygen'
          : redox !== null && redox !== undefined && redox !== 0 && withinSensorTarget(redox,sensorTargets.redox) === false
            ? 'redox'
            : salinity !== null && salinity !== undefined && withinSensorTarget(salinity,sensorTargets.salinity) === false
              ? 'salinity'
              : null
  ) : primaryLiveSensorType;
  const primaryBaseConfig = sensorConfigFor(primarySensorType);
  const primaryTarget=sensorTargets[primaryBaseConfig.key as SensorAuditMetric];
  const primarySensorConfig={...primaryBaseConfig,min:primaryTarget.min,max:primaryTarget.max,target:primaryTarget.label};
  const primaryCurrentReading = primarySensorConfig.key === 'temperature'
    ? liveTemperature
    : primarySensorConfig.key === 'ph'
      ? livePh
      : primarySensorConfig.key === 'oxygen'
        ? liveOxygen
        : primarySensorConfig.key === 'redox'
          ? liveRedox
          : primarySensorConfig.key === 'salinity'
            ? salinity
            : null;
  const primaryAlertReading = numberFrom(getMeta(primaryAlertCase, [
    'sensor_value',
    'sensorValue',
    'reading_value',
    'readingValue',
    'calibrated_value',
    'calibratedValue',
    'current_value',
    'currentValue',
    'value',
    'raw_value',
    'rawValue',
    primarySensorConfig.key
  ])) ?? (
    primarySensorConfig.key === 'temperature'
      ? primaryAlertCase?.temperature ?? temperature
      : primarySensorConfig.key === 'ph'
        ? primaryAlertCase?.ph ?? ph
        : primarySensorConfig.key === 'oxygen'
          ? primaryAlertCase?.oxygen ?? oxygen
          : primarySensorConfig.key === 'redox'
            ? numberFrom(getMeta(primaryAlertCase, ['redox', 'orp', 'redox_mv', 'orp_mv'])) ?? redox
            : primarySensorConfig.key === 'salinity'
              ? numberFrom(getMeta(primaryAlertCase, ['salinity', 'salinidade', 'salt'])) ?? salinity
              : null
  );
  const primaryReading = activeAlerts > 0
    ? primaryAlertReading ?? primaryCurrentReading
    : primaryCurrentReading;
  const primaryRule = activeAlerts > 0 ? cleanRuleText(
    getMeta(primaryAlertCase, ['rule_message', 'ruleMessage', 'message', 'description', 'problemDescription'])
      ?? primaryAlertCase?.rawAlert.problemName
      ?? primaryAlertCase?.suspectedPathology
  ) : null;
  const primaryReadingInTarget = primaryReading === null
    ? null
    : (primarySensorConfig.min === null || primaryReading >= primarySensorConfig.min) &&
      (primarySensorConfig.max === null || primaryReading <= primarySensorConfig.max);
  const primaryHasPendingResolvedReading = activeAlerts > 0 && primaryReadingInTarget === true;
  const primaryCardInAlert = activeAlerts > 0 && primaryReadingInTarget !== true;
  const primaryValueLabel = formatReading(primaryReading, primarySensorConfig.unit);
  const primaryRequiresAction = primaryCardInAlert || pendingValidation > 0;
  const primaryAuditMetric = (['temperature', 'ph', 'oxygen', 'redox', 'salinity'] as SensorAuditMetric[])
    .find((metric) => metric === primarySensorConfig.key);

  const currentReading = (directSensorReadings ?? [])
    .filter((row) => row.value !== null && row.value !== undefined)
    .filter((row) => primaryAuditMetric ? sensorTypeMatchesMetric(row.sensorType, primaryAuditMetric) : false)
    .sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    })[0]?.value ?? null;
  
  const currentReadingLabel = formatReading(currentReading, primarySensorConfig.unit);
  const currentReadingInTarget = currentReading === null
    ? null
    : (primarySensorConfig.min === null || currentReading >= primarySensorConfig.min) &&
      (primarySensorConfig.max === null || currentReading <= primarySensorConfig.max);
  const primaryEventTitle = primaryHasPendingResolvedReading
    ? `${primarySensorConfig.label} dentro do alvo operacional; alertas pendentes por validar`
    : activeAlerts > 0
    ? primaryRule ?? (
      primaryReading === null
        ? `${primarySensorConfig.label}: leitura a confirmar`
        : primaryDirection === 'lower'
          ? `${primarySensorConfig.label} abaixo do alvo biológico`
          : `${primarySensorConfig.label} acima do alvo biológico`
    )
    : primaryReading === null
      ? `${primarySensorConfig.label}: sem feed atual`
      : primaryReadingInTarget
        ? `${primarySensorConfig.label} dentro do alvo operacional`
        : `${primarySensorConfig.label} a monitorizar`;
  const visualEvidenceAvailable = hasVisualEvidence(primaryAlertCase?.rawAlert) || Boolean(latestCaptureView?.imageUrl || latestCaptureView?.videoUrl || latestCaptureView?.videoMp4Url || latestCaptureView?.videoWebmUrl);
  const latestAnalysisEmptyText = activeAlerts > 0 && !visualEvidenceAvailable
    ? 'Sem evidência visual recente para correlacionar com este alerta ambiental.'
    : t('vision', 'noRecentAnalysis', language);
  const statusTone = overall.label === t('vision', 'ok', language)
    ? 'emerald'
    : overall.label === t('vision', 'alert', language)
      ? 'red'
      : 'amber';
  const realTimeResult = activeAlerts > 0
    ? `${activeAlerts} ${activeAlerts === 1 ? t('vision', 'event', language) : t('vision', 'events', language)} ${t('vision', 'realTimeEvents', language)}`
    : t('vision', 'noActiveEvents', language);
  const sensorTrends = {
    temperature: temperature !== null && temperature !== undefined ? [temperature - 0.4, temperature - 0.2, temperature - 0.5, temperature - 0.1, temperature + 0.1, temperature, temperature + 0.3] : [0, 0.2, -0.1, 0.3, 0.1, 0.4, 0.2],
    ph: ph !== null && ph !== undefined ? [ph - 0.08, ph - 0.04, ph + 0.02, ph - 0.02, ph + 0.06, ph + 0.01, ph] : [0, 0.1, 0.05, 0.12, 0.08, 0.16, 0.1],
    oxygen: oxygen !== null && oxygen !== undefined ? [oxygen + 0.1, oxygen - 0.1, oxygen - 0.2, oxygen + 0.05, oxygen + 0.2, oxygen + 0.35, oxygen] : [0.2, 0.1, 0.25, 0.05, 0.3, 0.4, 0.32],
    redox: redox !== null && redox !== undefined ? [redox - 8, redox - 4, redox + 2, redox - 6, redox + 5, redox + 1, redox] : [0, 3, 2, 5, 4, 7, 6],
    salinity: salinity !== null && salinity !== undefined ? [salinity - 0.2, salinity - 0.1, salinity + 0.1, salinity, salinity + 0.2, salinity - 0.05, salinity] : [0, 0.1, 0.05, 0.12, 0.08, 0.16, 0.1]
  };
  const sensorAuditRows = buildSensorAuditRows(cases, {
    ...(sensorSummary ?? {}),
    sensorReadings: effectiveSensorReadings
  });
  const activeAuditConfig = activeSensorAudit ? {...SENSOR_AUDIT_CONFIG[activeSensorAudit],targetMin:sensorTargets[activeSensorAudit].min,targetMax:sensorTargets[activeSensorAudit].max,targetLabel:sensorTargets[activeSensorAudit].label} : null;
  const { start: auditStart, end: auditEnd } = rangeBounds(sensorAuditRange, sensorAuditDate);
  const filteredSensorAuditRows = activeSensorAudit
    ? sensorAuditRows
        .filter((row) => {
          const timestamp = new Date(row.timestamp).getTime();
          return Number.isFinite(timestamp) &&
            timestamp >= auditStart.getTime() &&
            timestamp < auditEnd.getTime() &&
            row.metrics[activeSensorAudit] !== null;
        })
        .sort((a, b) => {
          if (sensorAuditSort === 'oldest') return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          if (sensorAuditSort === 'value_desc') return (b.metrics[activeSensorAudit] ?? -Infinity) - (a.metrics[activeSensorAudit] ?? -Infinity);
          if (sensorAuditSort === 'value_asc') return (a.metrics[activeSensorAudit] ?? Infinity) - (b.metrics[activeSensorAudit] ?? Infinity);
          if (sensorAuditSort === 'tank') return `${a.tank} ${a.site}`.localeCompare(`${b.tank} ${b.site}`);
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        })
    : [];
  const sensorAuditChartData = activeSensorAudit
    ? buildSensorAuditChartData(filteredSensorAuditRows, activeSensorAudit, sensorAuditRange)
    : [];
  const sensorAuditValues = activeSensorAudit
    ? filteredSensorAuditRows
        .map((row) => row.metrics[activeSensorAudit])
        .filter((value): value is number => value !== null && Number.isFinite(value))
    : [];
  const sensorAuditStats = activeAuditConfig && sensorAuditValues.length > 0
    ? {
        average: sensorAuditValues.reduce((sum, value) => sum + value, 0) / sensorAuditValues.length,
        minimum: Math.min(...sensorAuditValues),
        maximum: Math.max(...sensorAuditValues),
        outsideTarget: sensorAuditValues.filter((value) =>
          value < activeAuditConfig.targetMin ||
          (activeAuditConfig.targetMax !== null && value > activeAuditConfig.targetMax)
        ).length
      }
    : null;
  const activeSensorCurrentValue = activeSensorAudit === 'temperature'
    ? temperature
    : activeSensorAudit === 'ph'
      ? ph
      : activeSensorAudit === 'oxygen'
        ? oxygen
        : activeSensorAudit === 'redox'
          ? redox
          : activeSensorAudit === 'salinity'
            ? salinity
            : null;

  useEffect(() => {
    if (!activeSensorAudit || sensorAuditRows.length === 0) return;
    const rowsForMetric = sensorAuditRows
      .filter((row) => row.metrics[activeSensorAudit] !== null)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    if (rowsForMetric.length === 0) return;

    const currentSelectionIsToday = sensorAuditDate === isoDateOnly(new Date());
    if (!currentSelectionIsToday) return;

    const latestMetricDate = isoDateOnly(new Date(rowsForMetric[0].timestamp));
    if (sensorAuditDate !== latestMetricDate) {
      setSensorAuditDate(latestMetricDate);
    }
  }, [activeSensorAudit, sensorAuditDate, sensorAuditRows]);

  const openSensorAudit = (metric: SensorAuditMetric) => {
    const latestMetricRow = sensorAuditRows.find((row) => row.metrics[metric] !== null);
    const latestMetricDate = latestMetricRow ? new Date(latestMetricRow.timestamp) : new Date();
    setActiveSensorAudit(metric);
    setSensorAuditDate(isoDateOnly(Number.isNaN(latestMetricDate.getTime()) ? new Date() : latestMetricDate));
    setSensorAuditRange('day');
    setSensorAuditSort('newest');
  };
  const primaryPulseColor = primaryCardInAlert
    ? '248, 113, 113'
    : '45, 212, 191';

  return (
    <div className="aql-vision-dashboard-root text-card-foreground animate-in fade-in duration-500">
      <style>{`
        .aql-vision-dashboard-root {
          container-type: inline-size;
          width: 100%;
          max-width: min(1680px, 100%);
          margin-left: auto;
          margin-right: auto;
        }
        .aql-vision-dashboard-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          grid-template-areas: "primary";
          gap: 1rem;
          align-items: stretch;
        }
        .aql-vision-dashboard-primary-grid {
          grid-template-columns: minmax(160px, 0.65fr) minmax(380px, 1.25fr) minmax(240px, 0.9fr);
          gap: clamp(1rem, 1.8cqw, 2rem);
        }
        .aql-vision-dashboard-primary-card {
          grid-area: primary;
          min-width: 0;
          min-height: clamp(400px, calc(100dvh - 176px), 780px);
          padding: clamp(1rem, 1.8cqw, 2rem);
        }
        .aql-vision-dashboard-primary-value { font-size: clamp(3rem, 6cqw, 6rem); }
        .aql-vision-dashboard-sensor-column { display: flex; flex-direction: column; gap: 1.25rem; }
        .aql-vision-dashboard-sensor-column > * { margin-block: 0; }
        .aql-vision-dashboard-actions { gap: 1rem; padding-top: 0.5rem; }
        .aql-vision-dashboard-actions > button { min-height: 44px; padding: 0.75rem 1rem; font-size: 0.8125rem; line-height: 1.2; }
        .aql-vision-dashboard-status > div:first-child > p { font-size: 0.6875rem; }
        .aql-vision-dashboard-status > div:first-child > div > span { font-size: 0.6875rem; padding-inline: 0.5rem; }
        .aql-vision-dashboard-primary-card::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          border-radius: inherit;
          border: 1px solid rgba(var(--aql-dashboard-pulse-rgb), 0);
          box-shadow: 0 0 0 0 rgba(var(--aql-dashboard-pulse-rgb), 0);
          opacity: 0;
          animation: aql-dashboard-primary-pulse 3.9s ease-in-out infinite;
        }
        @keyframes aql-dashboard-primary-pulse {
          0%, 100% {
            opacity: 0.08;
            border-color: rgba(var(--aql-dashboard-pulse-rgb), 0.10);
            box-shadow:
              0 0 0 0 rgba(var(--aql-dashboard-pulse-rgb), 0),
              inset 0 0 0 1px rgba(var(--aql-dashboard-pulse-rgb), 0.04);
          }
          34% {
            opacity: 1;
            border-color: rgba(var(--aql-dashboard-pulse-rgb), 0.58);
            box-shadow:
              0 0 0 4px rgba(var(--aql-dashboard-pulse-rgb), 0.12),
              0 0 34px rgba(var(--aql-dashboard-pulse-rgb), 0.24),
              inset 0 0 26px rgba(var(--aql-dashboard-pulse-rgb), 0.07);
          }
          68% {
            opacity: 0.44;
            border-color: rgba(var(--aql-dashboard-pulse-rgb), 0.28);
            box-shadow:
              0 0 0 10px rgba(var(--aql-dashboard-pulse-rgb), 0),
              0 0 22px rgba(var(--aql-dashboard-pulse-rgb), 0.12),
              inset 0 0 16px rgba(var(--aql-dashboard-pulse-rgb), 0.04);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .aql-vision-dashboard-primary-card::after {
            animation: none;
          }
        }
        .aql-vision-dashboard-status-card { grid-area: status; min-width: 0; }
        .aql-vision-dashboard-analysis-card { grid-area: analysis; min-width: 0; }
        .aql-vision-dashboard-sensors-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.85rem;
          min-width: 0;
        }
        .aql-vision-dashboard-sensor { min-width: 0; }
        .aql-vision-dashboard-primary-card .aql-vision-dashboard-sensor {
          min-height: 140px;
          padding: 1rem;
          overflow: visible;
        }
        .aql-vision-dashboard-sensor-label { font-size: 0.6875rem; line-height: 1.4; }
        .aql-vision-dashboard-sensor-value { font-size: 1.5rem; line-height: 1.1; }
        .aql-vision-dashboard-sensor-value > span { font-size: 0.75rem; }
        .aql-vision-dashboard-sensor-target { font-size: 0.75rem; line-height: 1.4; }
        .aql-vision-dashboard-sensor-badge { font-size: 0.625rem; line-height: 1.4; }
        .aql-vision-dashboard-sensor-column > p { font-size: 0.75rem; line-height: 1.5; }
        .aql-vision-dashboard-media,
        .aql-vision-dashboard-media > :is(img, video) { height: clamp(180px, 17cqw, 256px); }
        .aql-vision-dashboard-latest-grid {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: auto auto;
          gap: 0.75rem;
        }
        @container (max-width: 1240px) {
          .aql-vision-dashboard-primary-card .aql-vision-dashboard-sensor { min-height: 120px; padding: 0.75rem; }
          .aql-vision-dashboard-sensors-grid { gap: 0.65rem; }
          .aql-vision-dashboard-sensor-column { gap: 1rem; }
        }
        @container (max-width: 960px) {
          .aql-vision-dashboard-primary-grid { grid-template-columns: minmax(0, 1.35fr) minmax(240px, 1fr); }
          .aql-vision-dashboard-status {
            grid-column: 1 / -1;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
          }
          .aql-vision-dashboard-status > div:last-child { margin-top: 0; }
          .aql-vision-dashboard-primary-value { font-size: 3rem; }
          .aql-vision-dashboard-primary-card { min-height: 0; }
        }
        @container (max-width: 680px) {
          .aql-vision-dashboard-primary-grid { grid-template-columns: 1fr; }
          .aql-vision-dashboard-sensors-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .aql-vision-dashboard-primary-card { min-height: 0; }
          .aql-vision-dashboard-actions { flex-wrap: wrap; }
          .aql-vision-dashboard-actions > button { flex-basis: 180px; }
        }
        @media (max-height: 740px) and (min-width: 768px) {
          .aql-vision-dashboard-primary-card { min-height: 0; }
          .aql-vision-dashboard-primary-card .aql-vision-dashboard-sensor { min-height: 112px; padding: 0.5rem; }
          .aql-vision-dashboard-sensor-label { font-size: 0.65625rem; line-height: 1.25; }
          .aql-vision-dashboard-sensor-value { font-size: 1.375rem; }
          .aql-vision-dashboard-sensor-target { margin-top: 0.375rem; font-size: 0.6875rem; }
          .aql-vision-dashboard-sensor > div:last-child { margin-top: 0.375rem; }
          .aql-vision-dashboard-sensor-column { gap: 0.75rem; }
          .aql-vision-dashboard-actions { padding-top: 0; }
          .aql-vision-dashboard-demo-label { margin-bottom: 0.5rem; }
          .aql-vision-dashboard-demo-label > span { padding-block: 0.125rem; font-size: 0.6875rem; }
          .aql-vision-dashboard-latest-grid .aql-vision-dashboard-row { padding-block: 0.25rem; }
        }
      `}</style>

      {activeSensorAudit && activeAuditConfig ? createPortal(
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-950/42 px-3 py-6 backdrop-blur-sm sm:px-6">
          <section className="w-full max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)] dark:border-slate-700 dark:bg-slate-950">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 bg-slate-50/80 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/70">
              <div className="min-w-0">
                <p className="inline-flex items-center gap-2 text-[0.68rem] font-black uppercase tracking-[0.16em] text-cyan-700 dark:text-cyan-200">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Histórico de sensores
                </p>
                <h3 className="mt-1 text-xl font-black text-slate-950 dark:text-white">{activeAuditConfig.label}</h3>
                <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">
                  {filteredSensorAuditRows.length} entradas no intervalo selecionado. Valor atual do card: {formatSensorAuditValue(
                    activeSensorCurrentValue,
                    activeSensorAudit
                  )}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSensorAudit(null)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label="Fechar histórico de sensores"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800 lg:grid-cols-[auto_minmax(12rem,16rem)_minmax(12rem,16rem)] lg:items-end">
              <div>
                <p className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Intervalo</p>
                <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-900">
                  {[
                    ['day', 'Dia'],
                    ['week', 'Semana'],
                    ['month', 'Mês']
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSensorAuditRange(value as SensorAuditRange)}
                      className={`rounded-lg px-3 py-2 text-sm font-bold transition-colors ${
                        sensorAuditRange === value
                          ? 'bg-cyan-500 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-white hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="mb-2 block text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Dia de referência</span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="date"
                    value={sensorAuditDate}
                    onChange={(event) => setSensorAuditDate(event.target.value || isoDateOnly(new Date()))}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm font-semibold text-slate-900 outline-none transition-colors focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-[0.68rem] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Ordenar por</span>
                <select
                  value={sensorAuditSort}
                  onChange={(event) => setSensorAuditSort(event.target.value as SensorAuditSort)}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition-colors focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                >
                  <option value="newest">Mais recente primeiro</option>
                  <option value="oldest">Mais antigo primeiro</option>
                  <option value="value_desc">Valor mais alto</option>
                  <option value="value_asc">Valor mais baixo</option>
                  <option value="tank">Tanque / instalação</option>
                </select>
              </label>
            </div>

            {sensorAuditChartData.length > 0 && sensorAuditStats ? (
              <div className="border-b border-slate-200 bg-slate-50/45 px-5 py-5 dark:border-slate-800 dark:bg-slate-900/35">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-black text-slate-950 dark:text-white">Evolução temporal</h4>
                    <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {sensorAuditRange === 'day'
                        ? 'Média por blocos de 15 minutos'
                        : sensorAuditRange === 'week'
                          ? 'Média por blocos de 3 horas'
                          : 'Média diária'}
                      {' · '}Faixa recomendada: {activeAuditConfig.targetLabel}
                    </p>
                  </div>
                  <div className="grid min-w-full grid-cols-2 gap-2 sm:min-w-0 sm:grid-cols-4">
                    {[
                      ['Média', formatSensorAuditValue(sensorAuditStats.average, activeSensorAudit)],
                      ['Mínimo', formatSensorAuditValue(sensorAuditStats.minimum, activeSensorAudit)],
                      ['Máximo', formatSensorAuditValue(sensorAuditStats.maximum, activeSensorAudit)],
                      ['Fora do alvo', String(sensorAuditStats.outsideTarget)]
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/65">
                        <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">{label}</span>
                        <span className={`mt-0.5 block text-sm font-black ${
                          label === 'Fora do alvo' && sensorAuditStats.outsideTarget > 0
                            ? 'text-amber-600 dark:text-amber-300'
                            : 'text-slate-950 dark:text-white'
                        }`}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sensorAuditChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(148,163,184,0.24)" strokeDasharray="3 5" vertical={false} />
                      <XAxis
                        dataKey="label"
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={30}
                        tickMargin={9}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        width={52}
                        tickMargin={8}
                        domain={['auto', 'auto']}
                        tickFormatter={(value) => activeSensorAudit === 'ph' ? Number(value).toFixed(1) : String(Math.round(Number(value) * 10) / 10)}
                      />
                      {activeAuditConfig.targetMax !== null ? (
                        <ReferenceArea
                          y1={activeAuditConfig.targetMin}
                          y2={activeAuditConfig.targetMax}
                          fill="#34d399"
                          fillOpacity={0.10}
                          stroke="#34d399"
                          strokeOpacity={0.28}
                          ifOverflow="extendDomain"
                        />
                      ) : (
                        <ReferenceLine
                          y={activeAuditConfig.targetMin}
                          stroke="#34d399"
                          strokeWidth={1.5}
                          strokeDasharray="5 5"
                          ifOverflow="extendDomain"
                          label={{ value: `Mín. ${activeAuditConfig.targetLabel}`, position: 'insideTopRight', fill: '#10b981', fontSize: 11 }}
                        />
                      )}
                      <Tooltip
                        cursor={{ stroke: activeAuditConfig.color, strokeDasharray: '3 3', strokeOpacity: 0.55 }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const point = payload[0].payload as { timestamp: number; value: number; samples: number };
                          const outsideTarget = point.value < activeAuditConfig.targetMin ||
                            (activeAuditConfig.targetMax !== null && point.value > activeAuditConfig.targetMax);
                          return (
                            <div className="min-w-52 rounded-xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-950">
                              <p className="text-xs font-bold text-slate-950 dark:text-white">{formatDateTime(new Date(point.timestamp).toISOString(), language)}</p>
                              <p className="mt-2 text-lg font-black" style={{ color: activeAuditConfig.color }}>
                                {formatSensorAuditValue(point.value, activeSensorAudit)}
                              </p>
                              <div className="mt-2 flex items-center justify-between gap-3 text-[0.68rem] font-semibold text-slate-500 dark:text-slate-400">
                                <span>{point.samples} {point.samples === 1 ? 'leitura' : 'leituras'}</span>
                                <span className={outsideTarget ? 'text-amber-600 dark:text-amber-300' : 'text-emerald-600 dark:text-emerald-300'}>
                                  {outsideTarget ? 'Fora do alvo' : 'Dentro do alvo'}
                                </span>
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={activeAuditConfig.color}
                        strokeWidth={2.5}
                        dot={sensorAuditChartData.length <= 48 ? { r: 2.5, fill: activeAuditConfig.color, strokeWidth: 0 } : false}
                        activeDot={{ r: 4.5, fill: activeAuditConfig.color, stroke: 'var(--card)', strokeWidth: 2 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : null}

            <div className="max-h-[62vh] overflow-auto">
              {filteredSensorAuditRows.length > 0 ? (
                <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-100 text-[0.68rem] uppercase tracking-[0.12em] text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-3 font-black">Data / hora</th>
                      <th className="px-5 py-3 font-black">Tanque</th>
                      <th className="px-5 py-3 font-black">Valor</th>
                      <th className="px-5 py-3 font-black">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredSensorAuditRows.map((row) => (
                      <tr key={`${row.id}-${row.timestamp}`} className="bg-white transition-colors hover:bg-cyan-50/45 dark:bg-slate-950 dark:hover:bg-slate-900">
                        <td className="px-5 py-3 font-semibold text-slate-900 dark:text-white">{formatDateTime(row.timestamp, language)}</td>
                        <td className="px-5 py-3 text-slate-700 dark:text-slate-300">
                          <span className="block font-bold text-slate-900 dark:text-white">{row.tank}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{row.site}</span>
                        </td>
                        <td className="px-5 py-3 text-lg font-black text-cyan-700 dark:text-cyan-200">{formatSensorAuditValue(row.metrics[activeSensorAudit], activeSensorAudit)}</td>
                        <td className="px-5 py-3">
                          <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex min-h-52 flex-col items-center justify-center px-5 py-10 text-center">
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                  <p className="mt-3 text-base font-black text-slate-950 dark:text-white">Sem entradas para este filtro</p>
                  <p className="mt-1 max-w-md text-sm text-slate-600 dark:text-slate-300">
                    Ajusta o dia de referência, muda para semana/mês.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>,
        document.body
      ) : null}

      <section className="aql-vision-dashboard-layout">
        <section
          className={`aql-vision-dashboard-primary-card relative overflow-hidden rounded-2xl border p-6 md:p-8 shadow-sm ring-1 backdrop-blur-sm ${
            primaryCardInAlert
              ? 'border-red-300/55 bg-[radial-gradient(circle_at_12%_0%,rgba(248,113,113,0.22),transparent_34%),linear-gradient(135deg,rgba(254,242,242,0.96),rgba(255,255,255,0.96)_46%,rgba(248,250,252,0.96))] ring-red-200/50 dark:border-red-400/30 dark:bg-[radial-gradient(circle_at_12%_0%,rgba(239,68,68,0.20),transparent_34%),linear-gradient(135deg,rgba(127,29,29,0.22),rgba(15,23,42,0.88)_45%,rgba(8,13,28,0.94))] dark:ring-red-500/15'
              : 'border-emerald-300/45 bg-[linear-gradient(135deg,rgba(236,253,245,0.96),rgba(255,255,255,0.96)_48%,rgba(248,250,252,0.96))] ring-emerald-200/50 dark:border-emerald-400/20 dark:bg-[linear-gradient(135deg,rgba(16,185,129,0.12),rgba(15,23,42,0.88)_48%,rgba(8,13,28,0.94))] dark:ring-emerald-500/15'
          }`}
          style={{ '--aql-dashboard-pulse-rgb': primaryPulseColor } as CSSProperties}
          data-demo-tour="command-status"
        >
          <div className="aql-vision-dashboard-primary-grid relative z-10 grid gap-8 lg:items-start">
            <div className="aql-vision-dashboard-status min-w-0">
              <div>
              <p className="mb-2 text-[0.68rem] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                Estado
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] ${
                  primaryCardInAlert
                    ? 'border-red-300/70 bg-red-50 text-red-700 dark:border-red-300/45 dark:bg-red-500/14 dark:text-red-100'
                    : 'border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-300/45 dark:bg-emerald-500/14 dark:text-emerald-100'
                }`}>
                  {primaryCardInAlert ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  {primaryCardInAlert ? 'Evento ativo' : 'Leitura normal'}
                </span>
                <StatusValueBadge tone={primaryCardInAlert ? statusTone : 'emerald'}>
                  {primaryCardInAlert ? overall.label : 'Dentro do alvo'}
                </StatusValueBadge>
                {primaryHasPendingResolvedReading ? (
                  <StatusValueBadge tone="slate">{pendingValidation} por validar</StatusValueBadge>
                ) : null}
                {primaryCardInAlert && primaryAlertCase?.severity ? (
                  <StatusValueBadge tone={primaryAlertCase.severity === 'critical' ? 'red' : primaryAlertCase.severity === 'high' ? 'amber' : 'slate'}>
                    {primaryAlertCase.severity === 'critical' ? 'Crítica' : primaryAlertCase.severity === 'high' ? 'Alta' : primaryAlertCase.severity}
                  </StatusValueBadge>
                ) : null}
              </div>
              </div>
              <div className="mt-5 flex flex-wrap items-end gap-x-5 gap-y-2">
                <div className="aql-vision-dashboard-primary-value whitespace-nowrap font-black leading-none text-slate-950 dark:text-white">{primaryValueLabel}</div>
                <div className="pb-1 text-lg font-extrabold text-cyan-700 dark:text-cyan-100">{primarySensorConfig.label}</div>
              </div>

            </div>

            <div className="aql-vision-dashboard-sensor-column min-w-0">
              <section aria-label="Dados físico-químicos" data-operational-sensors>
                {isDemonstration ? (
                  <div className="aql-vision-dashboard-demo-label mb-3 flex flex-wrap items-center justify-between gap-2">
                    <StatusValueBadge tone="slate">Demonstração</StatusValueBadge>
                  </div>
                ) : null}
          <div className="aql-vision-dashboard-sensors-grid">
            <SensorSignalCard
              compact
              icon={Thermometer}
              label={t('vision', 'temperature', language)}
              value={temperature !== null && temperature !== undefined ? temperature.toFixed(1) : '--'}
              unit="°C"
              target={sensorTargets.temperature.label}
              hasFeed={hasLiveTemperature}
              sourceLabel={!hasLiveTemperature && temperature !== null && temperature !== undefined ? 'Último alerta' : undefined}
              tone="blue"
              trend={sensorTrends.temperature}
              isCompliant={temperature !== null && temperature !== undefined ? withinSensorTarget(temperature,sensorTargets.temperature) : undefined}
              language={language}
              onClick={() => openSensorAudit('temperature')}
            />
            <SensorSignalCard
              compact
              icon={FlaskConical}
              label={t('vision', 'ph', language)}
              value={ph !== null && ph !== undefined ? ph.toFixed(2) : '--'}
              target={sensorTargets.ph.label}
              hasFeed={hasLivePh}
              sourceLabel={demoMetrics.has('ph') ? 'Simulado' : !hasLivePh && ph !== null && ph !== undefined ? 'Último alerta' : undefined}
              tone="cyan"
              trend={sensorTrends.ph}
              isCompliant={ph !== null && ph !== undefined ? withinSensorTarget(ph,sensorTargets.ph) : undefined}
              language={language}
              onClick={() => openSensorAudit('ph')}
            />
            <SensorSignalCard
              compact
              icon={Droplets}
              label={t('vision', 'dissolvedOxygen', language)}
              value={oxygen !== null && oxygen !== undefined ? oxygen.toFixed(1) : '--'}
              unit="mg/L"
              target={sensorTargets.oxygen.label}
              hasFeed={hasLiveOxygen}
              sourceLabel={demoMetrics.has('oxygen') ? 'Simulado' : !hasLiveOxygen && oxygen !== null && oxygen !== undefined ? 'Último alerta' : undefined}
              tone="purple"
              trend={sensorTrends.oxygen}
              isCompliant={oxygen !== null && oxygen !== undefined ? withinSensorTarget(oxygen,sensorTargets.oxygen) : undefined}
              language={language}
              onClick={() => openSensorAudit('oxygen')}
            />
            <SensorSignalCard
              compact
              icon={Zap}
              label={t('vision', 'redoxORP', language)}
              value={redox !== null && redox !== undefined && redox !== 0 ? redox.toFixed(0) : '--'}
              unit="mV"
              target={sensorTargets.redox.label}
              hasFeed={hasLiveRedox}
              sourceLabel={demoMetrics.has('redox') ? 'Simulado' : !hasLiveRedox && redox !== null && redox !== undefined && redox !== 0 ? 'Último alerta' : undefined}
              tone="emerald"
              trend={sensorTrends.redox}
              isCompliant={redox !== null && redox !== undefined && redox !== 0 ? withinSensorTarget(redox,sensorTargets.redox) : undefined}
              language={language}
              onClick={() => openSensorAudit('redox')}
            />
            <SensorSignalCard
              compact
              icon={Waves}
              label="Salinidade"
              value={salinity !== null && salinity !== undefined ? salinity.toFixed(1) : '--'}
              unit="PSU"
              target={sensorTargets.salinity.label}
              hasFeed={hasLiveSalinity}
              sourceLabel={demoMetrics.has('salinity') ? 'Simulado' : !hasLiveSalinity && salinity !== null && salinity !== undefined ? 'Último alerta' : undefined}
              tone="cyan"
              trend={sensorTrends.salinity}
              isCompliant={salinity !== null && salinity !== undefined ? withinSensorTarget(salinity,sensorTargets.salinity) : undefined}
              language={language}
              onClick={() => openSensorAudit('salinity')}
            />
          </div>
              </section>
              <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{t('vision','lastUpdate',language)}: {formatDateTime(sensorSummary?.updatedAt ?? latestUpdate,language)}</p>

              <div className="aql-vision-dashboard-actions flex flex-wrap">
                <button
                  type="button"
                  onClick={onOpenAlerts}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border px-5 py-3.5 text-sm font-extrabold transition-colors ${
                    primaryRequiresAction
                      ? 'border-red-500 bg-red-500 text-white hover:border-red-600 hover:bg-red-600 dark:border-red-400 dark:bg-red-500 dark:hover:bg-red-400'
                      : 'border-cyan-300/60 bg-cyan-50 text-cyan-800 hover:border-cyan-400/80 hover:bg-cyan-100 dark:border-cyan-300/45 dark:bg-cyan-400/[0.10] dark:text-cyan-100 dark:hover:border-cyan-200/70 dark:hover:bg-cyan-400/[0.16]'
                  }`}
                >
                  <AlertTriangle className="h-4 w-4" />
                  {primaryRequiresAction ? 'Rever e validar alerta' : 'Ver alertas'}
                </button>
                {onOpenLiveCamera ? (
                  <button
                    type="button"
                    onClick={onOpenLiveCamera}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-300/60 bg-emerald-50 px-5 py-3.5 text-sm font-extrabold text-emerald-800 transition-colors hover:border-emerald-400/80 hover:bg-emerald-100 dark:border-emerald-300/45 dark:bg-emerald-400/[0.10] dark:text-emerald-100 dark:hover:border-emerald-200/70 dark:hover:bg-emerald-400/[0.16]"
                  >
                    <Video className="h-4 w-4" />
                    Unidades
                  </button>
                ) : null}
              </div>
            </div>
        <section aria-label="Correlação visual" className="min-w-0" data-demo-tour="latest-analysis">
          {latestCaptureView ? (
            <div className="aql-vision-dashboard-latest-grid">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-muted shadow-sm dark:border-[#263753] dark:bg-slate-950 dark:shadow-[0_12px_28px_rgba(2,6,23,0.28)]">
                {latestCaptureView.imageUrl || latestCaptureView.videoUrl || latestCaptureView.videoMp4Url || latestCaptureView.videoWebmUrl ? (
                  <LatestCaptureMedia
                    capture={latestCaptureView}
                    label={t('vision', 'latestAnalysis', language)}
                    onOpen={onOpenLatestCapture ? () => onOpenLatestCapture(latestCaptureAlertId) : undefined}
                    language={language}
                  />
                ) : (
                  <div className="flex h-56 flex-col items-center justify-center px-5 text-center text-sm text-muted-foreground">
                    <p className="max-w-sm">{latestAnalysisEmptyText}</p>
                    {onOpenLiveCamera ? (
                      <button
                        type="button"
                        onClick={onOpenLiveCamera}
                        className="mt-4 inline-flex items-center gap-2 rounded-xl border border-emerald-300/55 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:border-emerald-400 hover:bg-emerald-100 dark:border-emerald-400/25 dark:bg-emerald-400/[0.09] dark:text-emerald-100 dark:hover:border-emerald-300/40 dark:hover:bg-emerald-400/[0.16]"
                      >
                        <Video className="h-4 w-4" />
                        {t('vision', 'openLiveCamera', language)}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <MetricRow compact label={t('vision', 'timestamp', language)} value={formatDateTime(latestCaptureView.capturedAt, language)} />
                <MetricRow compact label={t('vision', 'tank', language)} value={latestCaptureView.tank ?? '—'} />
                <MetricRow compact label={t('vision', 'species', language)} value={latestCaptureView.species ?? '—'} />
                <MetricRow compact label={t('vision', 'phase', language)} value={latestCaptureView.phase ?? '—'} />
                <MetricRow compact label={t('vision', 'detection', language)} value={latestCaptureView.detectionStatus || '—'} />
              </div>
            </div>
          ) : (
            <div className="flex min-h-56 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/60 px-5 py-10 text-center text-sm text-muted-foreground dark:border-[#263753] dark:bg-[#101a2d] dark:text-slate-400">
              <p className="max-w-sm">{latestAnalysisEmptyText}</p>
              {onOpenLiveCamera ? (
                <button
                  type="button"
                  onClick={onOpenLiveCamera}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl border border-emerald-300/55 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:border-emerald-400 hover:bg-emerald-100 dark:border-emerald-400/25 dark:bg-emerald-400/[0.09] dark:text-emerald-100 dark:hover:border-emerald-300/40 dark:hover:bg-emerald-400/[0.16]"
                >
                  <Video className="h-4 w-4" />
                  {t('vision', 'openLiveCamera', language)}
                </button>
              ) : null}
            </div>
          )}
        </section>
          </div>
        </section>






      </section>
    </div>
  );
}
